// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { AlertTriangle, MessageSquare, X } from 'lucide-react';

import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { DOCS_LINKS } from '@/lib/docsLinks';
import { cn } from '@/lib/utils';
import type { OrgSettings, SetSlackWebhook, UpdateNotificationsSettings } from '@greenlight/shared';

import { SettingsSectionHeading } from './SettingsSectionHeading';
import { ToggleRow } from './ToggleRow';

/** Writable notification categories exposed in Settings (docs/28). */
type WritableNotificationCategories = {
  health_alerts: boolean;
  health_alert_email: boolean;
  permission_requests: boolean;
  connection_requests: boolean;
  weekly_digest: boolean;
};

// ── Props ─────────────────────────────────────────────────────────────────────

export interface NotificationsSettingsProps {
  notifications: OrgSettings['notifications'];
  onUpdate: (patch: UpdateNotificationsSettings) => Promise<void> | void;
  /** Registers or replaces the Slack incoming-webhook URL. Write-only. */
  onSetSlackWebhook: (input: SetSlackWebhook) => Promise<void> | void;
  onRemoveSlackWebhook: () => Promise<void> | void;
  className?: string;
}

// ── Email recipient chip ───────────────────────────────────────────────────────

interface RecipientChipProps {
  email: string;
  onRemove: () => void;
}

function RecipientChip({ email, onRemove }: RecipientChipProps): React.JSX.Element {
  return (
    <span className="inline-flex items-center gap-1 rounded-gl bg-muted border border-border px-2 py-1 text-[13px]">
      <span className="font-mono">{email}</span>
      <button
        type="button"
        aria-label={`Remove ${email}`}
        onClick={onRemove}
        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    </span>
  );
}

// ── Email recipients card ─────────────────────────────────────────────────────

interface EmailRecipientsCardProps {
  emailRecipients: string[];
  onUpdate: (patch: UpdateNotificationsSettings) => Promise<void> | void;
}

function EmailRecipientsCard({
  emailRecipients,
  onUpdate,
}: EmailRecipientsCardProps): React.JSX.Element {
  const inputId = React.useId();
  const [inputValue, setInputValue] = React.useState('');

  function isValidEmail(value: string): boolean {
    return value.includes('@') && value.trim().length > 0;
  }

  function isDuplicate(value: string): boolean {
    return emailRecipients.includes(value.trim());
  }

  function handleAdd(): void {
    const trimmed = inputValue.trim();
    if (!isValidEmail(trimmed) || isDuplicate(trimmed)) return;
    void onUpdate({ email_recipients: [...emailRecipients, trimmed] });
    setInputValue('');
  }

  function handleRemove(email: string): void {
    void onUpdate({ email_recipients: emailRecipients.filter((e) => e !== email) });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  }

  const trimmedInput = inputValue.trim();
  const canAdd = isValidEmail(trimmedInput) && !isDuplicate(trimmedInput);

  return (
    <SectionCard headerBar title="Email recipients">
      <div className="px-5 py-3.5 space-y-3">
        {/* Chips */}
        {emailRecipients.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {emailRecipients.map((email) => (
              <RecipientChip key={email} email={email} onRemove={() => handleRemove(email)} />
            ))}
          </div>
        )}

        {/* Add recipient input */}
        <div className="flex items-center gap-2">
          <label htmlFor={inputId} className="sr-only">
            Add recipient by email
          </label>
          <Input
            id={inputId}
            type="email"
            placeholder="Add recipient by email"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-11 md:h-9 flex-1"
            aria-label="Add recipient by email"
          />
          <Button
            variant="outline"
            size="default"
            className="h-11 md:h-9 shrink-0"
            onClick={handleAdd}
            disabled={!canAdd}
          >
            Add
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}

// ── Slack destination card ────────────────────────────────────────────────────

/** Human wording for the failure reasons the Slack sender records. */
const SLACK_FAILURE_COPY: Record<string, string> = {
  webhook_revoked: 'Slack rejected the webhook. It was probably revoked or deleted.',
  channel_not_found: 'The channel this webhook posts to no longer exists.',
  channel_is_archived: 'The channel this webhook posts to has been archived.',
  no_service: 'Slack no longer recognises this webhook.',
  not_configured: 'The destination is switched on but no webhook URL is stored.',
  slack_unavailable: 'Slack could not be reached on the last attempt.',
  unreachable: 'Slack could not be reached on the last attempt.',
};

interface SlackDestinationCardProps {
  slack: NonNullable<OrgSettings['notifications']>['slack'];
  onUpdate: (patch: UpdateNotificationsSettings) => Promise<void> | void;
  onSetWebhook: (input: SetSlackWebhook) => Promise<void> | void;
  onRemoveWebhook: () => Promise<void> | void;
}

const absolute = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function SlackDestinationCard({
  slack,
  onUpdate,
  onSetWebhook,
  onRemoveWebhook,
}: SlackDestinationCardProps): React.JSX.Element {
  const urlId = React.useId();
  const hintId = React.useId();
  const [editing, setEditing] = React.useState(false);
  const [url, setUrl] = React.useState('');
  const [hint, setHint] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [confirmingRemove, setConfirmingRemove] = React.useState(false);
  const [removing, setRemoving] = React.useState(false);
  const [removeError, setRemoveError] = React.useState<string | null>(null);

  const configured = slack !== null;
  const failure = slack?.last_failure ?? null;

  function close(): void {
    setEditing(false);
    setUrl('');
    setHint('');
    setError(null);
  }

  async function handleSave(): Promise<void> {
    const trimmed = url.trim();
    // Mirrors the server's own check so a typo is caught before the URL is sent
    // anywhere. The server re-validates; this is only for the error message.
    if (!/^https:\/\/hooks\.slack\.com\//.test(trimmed)) {
      setError('Enter the https://hooks.slack.com/… URL Slack gave you for the channel.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSetWebhook({ webhook_url: trimmed, channel_hint: hint.trim() || null });
      close();
    } catch {
      setError('Could not save the webhook. Try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(): Promise<void> {
    setRemoving(true);
    setRemoveError(null);
    try {
      await onRemoveWebhook();
      setConfirmingRemove(false);
    } catch {
      setRemoveError('Could not remove the webhook. It is still registered. Try again.');
    } finally {
      setRemoving(false);
    }
  }

  return (
    <SectionCard headerBar title="Slack">
      <div className="px-5 py-3.5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-sm font-medium text-foreground">
                {configured ? (slack.channel_hint ?? 'Incoming webhook') : 'Not configured'}
              </span>
              <span className="text-xs text-muted-foreground">
                {configured
                  ? 'Posts the categories below to this channel.'
                  : 'Add a Slack incoming webhook to post notifications to a channel.'}
              </span>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2 pl-3">
            <Button
              variant="outline"
              size="sm"
              className="h-11 md:h-9"
              aria-label={configured ? 'Replace Slack webhook' : 'Add Slack webhook'}
              onClick={() => {
                if (editing) return close();
                setHint(slack?.channel_hint ?? '');
                setEditing(true);
              }}
            >
              {configured ? 'Replace' : 'Add'}
            </Button>
            {configured && (
              <Button
                variant="outline"
                size="sm"
                className="h-11 md:h-9"
                aria-label="Remove Slack webhook"
                onClick={() => {
                  setRemoveError(null);
                  setConfirmingRemove(true);
                }}
              >
                Remove
              </Button>
            )}
            <Switch
              checked={slack?.enabled ?? false}
              disabled={!configured}
              onCheckedChange={(value) => {
                if (!configured) return;
                void Promise.resolve(onUpdate({ slack: { enabled: value } })).catch(
                  () => undefined,
                );
              }}
              aria-label="Enable Slack notifications"
              className="shrink-0"
            />
          </div>
        </div>

        {failure && slack?.enabled === true && (
          <Alert variant="warning" data-testid="notifications-slack-failure">
            <AlertTriangle aria-hidden="true" />
            <AlertTitle>Slack is not receiving these</AlertTitle>
            <AlertDescription>
              <p>
                {SLACK_FAILURE_COPY[failure.reason] ?? 'The last Slack post did not go through.'}{' '}
                Last attempt {absolute(failure.at)}. Replace the webhook URL below; this notice
                clears as soon as you do, and comes back if the next post fails too.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {editing && (
          <div className="space-y-2 rounded-gl border border-border p-3">
            <label htmlFor={urlId} className="block text-xs font-medium text-foreground">
              Slack incoming-webhook URL
            </label>
            <Input
              id={urlId}
              type="url"
              autoComplete="off"
              placeholder="https://hooks.slack.com/services/…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="h-11 md:h-9 font-mono"
            />
            <label htmlFor={hintId} className="block text-xs font-medium text-foreground">
              Channel name (optional, so the row is recognisable)
            </label>
            <Input
              id={hintId}
              placeholder="#greenlight-alerts"
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              className="h-11 md:h-9"
            />
            <p className="text-xs text-muted-foreground">
              Greenlight stores this URL in the vault and never shows it again. Anyone holding it
              can post to the channel, so replace it rather than reading it back.
            </p>
            {error !== null && (
              <p role="alert" className="text-xs text-destructive">
                {error}
              </p>
            )}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="h-11 md:h-9"
                aria-label="Save Slack webhook"
                disabled={saving}
                onClick={() => void handleSave()}
              >
                Save
              </Button>
              <Button variant="outline" size="sm" className="h-11 md:h-9" onClick={close}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
      <GLDrawer
        variant="confirm"
        mobileBreakpoint={768}
        open={confirmingRemove}
        onOpenChange={(open) => {
          if (!open) setConfirmingRemove(false);
        }}
        preventDismiss={removing}
        title="Remove the Slack webhook?"
        description={slack?.channel_hint ?? 'Incoming webhook'}
        contentProps={{ 'data-testid': 'remove-slack-webhook-dialog' }}
        footer={
          <>
            <Button
              variant="outline"
              className="h-11 md:h-9"
              disabled={removing}
              onClick={() => setConfirmingRemove(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="h-11 md:h-9"
              disabled={removing}
              onClick={() => void handleRemove()}
              data-testid="confirm-remove-slack-webhook"
            >
              {removing ? 'Removing…' : 'Remove webhook'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm leading-relaxed">
          <p>
            Greenlight stops posting to this channel and deletes the stored URL. It cannot be shown
            or recovered, so posting here again means creating a new incoming webhook in Slack and
            registering it.
          </p>
          {removeError !== null && (
            <p role="alert" className="text-destructive">
              {removeError}
            </p>
          )}
        </div>
      </GLDrawer>
    </SectionCard>
  );
}

// ── No-sender steps ───────────────────────────────────────────────────────────

const mono = (text: string): React.JSX.Element => <span className="font-mono">{text}</span>;

function GuideLink({ href }: { href: string }): React.JSX.Element {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="text-link underline">
      How notification email is set up
    </a>
  );
}

type InstallCloud = NonNullable<OrgSettings['notifications']>['cloud'];
type EmailFailure = NonNullable<NonNullable<OrgSettings['notifications']>['email_last_failure']>;

/** Where each cloud's install keeps the relay password, and its notification email guide. */
const RELAY_VAULT: Record<
  Exclude<InstallCloud, 'localfake'>,
  { store: string; secret: string; guide: string }
> = {
  azure: {
    store: 'Key Vault',
    secret: 'notifications-smtp-password',
    guide: DOCS_LINKS.notificationEmailAzure,
  },
  gcp: {
    store: 'Secret Manager',
    secret: 'notifications-smtp-password',
    guide: DOCS_LINKS.notificationEmailGcp,
  },
  aws: {
    store: 'Secrets Manager',
    secret: 'notifications/smtp-password',
    guide: DOCS_LINKS.notificationEmailAws,
  },
};

function SenderSteps({ cloud }: { cloud?: InstallCloud }): React.JSX.Element {
  if (cloud === 'azure') {
    return (
      <>
        <p>
          The install sets up its own Azure email sender once the Microsoft.Communication resource
          provider is registered on its subscription. A subscription administrator runs{' '}
          {mono(
            'az provider register --namespace Microsoft.Communication --subscription <subscription ID>',
          )}
          , and the next update, automatic or a run of the installer, sets up the sender.
        </p>
        <p>
          In a region where Azure keeps no email data, run the installer again with your SMTP relay
          instead, {mono('--email-smtp-host')} and {mono('--email-sender')}, and if the relay needs
          a sign-in, put its password in the install&apos;s Key Vault as{' '}
          {mono('notifications-smtp-password')}.{' '}
          <GuideLink href={DOCS_LINKS.notificationEmailAzure} />
        </p>
      </>
    );
  }
  if (cloud === 'gcp' || cloud === 'aws') {
    const { store, secret, guide } = RELAY_VAULT[cloud];
    return (
      <p>
        Whoever runs the install can run the installer again with your SMTP relay,{' '}
        {mono('--email-smtp-host')} and {mono('--email-sender')}, and if the relay needs a sign-in,
        put its password in the install&apos;s {store} as {mono(secret)}. <GuideLink href={guide} />
      </p>
    );
  }
  return <p>Whoever runs the install can set up a sender by running the installer again.</p>;
}

// ── Failing sender ────────────────────────────────────────────────────────────

function FailureCause({
  reason,
  cloud,
  transport,
}: {
  reason: EmailFailure['reason'];
  cloud?: InstallCloud;
  transport?: NonNullable<OrgSettings['notifications']>['email_transport'];
}): React.JSX.Element {
  if (transport === 'acs') {
    return reason === 'sign_in_refused' ? (
      <>
        Azure refused to let the install send. The install&apos;s identity needs its Communication
        and Email Service Owner role on the install&apos;s email service; an update or a run of the
        installer restores it.
      </>
    ) : (
      <>The last notification email could not be sent.</>
    );
  }
  const vault = cloud && cloud !== 'localfake' ? RELAY_VAULT[cloud] : undefined;
  const where = vault ? (
    <>
      the install&apos;s {vault.store} as {mono(vault.secret)}
    </>
  ) : (
    <>the install&apos;s vault</>
  );
  switch (reason) {
    case 'password_missing':
      return <>The relay needs a password that is not stored. Put it in {where}.</>;
    case 'sign_in_refused':
      return (
        <>
          The relay refused the sign-in. Check the relay username the installer recorded and the
          password in {where}.
        </>
      );
    case 'relay_unreachable':
      return (
        <>
          The install could not reach the relay. Check its host and port, and that the
          install&apos;s network lets mail out to it.
        </>
      );
    default:
      return <>The last notification email could not be sent.</>;
  }
}

// ── Main component ────────────────────────────────────────────────────────────

export function NotificationsSettings({
  notifications,
  onUpdate,
  onSetSlackWebhook,
  onRemoveSlackWebhook,
  className,
}: NotificationsSettingsProps): React.JSX.Element {
  // Local state prevents stale-prop races: if two patches fire before a
  // refetch resolves, each one is built from the latest local value rather
  // than from a props snapshot that hasn't received the first save yet.
  const [localCategories, setLocalCategories] = React.useState<WritableNotificationCategories>(
    () => ({
      health_alerts: notifications?.categories.health_alerts ?? false,
      health_alert_email: notifications?.categories.health_alert_email ?? false,
      permission_requests: notifications?.categories.permission_requests ?? false,
      connection_requests: notifications?.categories.connection_requests ?? false,
      weekly_digest: notifications?.categories.weekly_digest ?? false,
    }),
  );
  const [localEmails, setLocalEmails] = React.useState(notifications?.email_recipients ?? []);
  const desiredCategoriesRef = React.useRef(localCategories);
  const desiredEmailsRef = React.useRef(localEmails);

  const savedHealth = notifications?.categories.health_alerts;
  const savedHealthEmail = notifications?.categories.health_alert_email;
  const savedPermissions = notifications?.categories.permission_requests;
  const savedConnections = notifications?.categories.connection_requests;
  const savedDigest = notifications?.categories.weekly_digest;
  const savedEmailKey = (notifications?.email_recipients ?? []).join('\0');

  React.useEffect(() => {
    if (
      savedHealth !== undefined &&
      savedHealthEmail !== undefined &&
      savedPermissions !== undefined &&
      savedConnections !== undefined &&
      savedDigest !== undefined
    ) {
      const saved = {
        health_alerts: savedHealth,
        health_alert_email: savedHealthEmail,
        permission_requests: savedPermissions,
        connection_requests: savedConnections,
        weekly_digest: savedDigest,
      };
      desiredCategoriesRef.current = saved;
      setLocalCategories(saved);
    }
  }, [savedHealth, savedHealthEmail, savedPermissions, savedConnections, savedDigest]);

  React.useEffect(() => {
    if (notifications?.email_recipients !== undefined) {
      desiredEmailsRef.current = notifications.email_recipients;
      setLocalEmails(notifications.email_recipients);
    }
  }, [savedEmailKey]);

  // Unconfigured state — section not yet returned by the server
  if (notifications === undefined) {
    return (
      <div className={cn('space-y-6', className)}>
        <SettingsSectionHeading title="Notifications" pill="IT" />
        <SectionCard headerBar title="Notifications">
          <div className="px-5 py-4 text-sm text-muted-foreground">Not configured yet.</div>
        </SectionCard>
      </div>
    );
  }

  const {
    slack,
    email_delivery_configured: emailDeliveryConfigured,
    cloud,
    email_last_failure: emailFailure,
  } = notifications;
  const guide = cloud !== 'localfake' ? RELAY_VAULT[cloud].guide : undefined;
  // Health alerts only reach email when their email switch is on too.
  const anyCategoryOn =
    (localCategories.health_alerts && localCategories.health_alert_email) ||
    localCategories.permission_requests ||
    localCategories.connection_requests ||
    localCategories.weekly_digest;

  const handleEmailUpdate = async (patch: UpdateNotificationsSettings): Promise<void> => {
    const prevEmails = localEmails;
    if (patch.email_recipients !== undefined) {
      desiredEmailsRef.current = patch.email_recipients;
      setLocalEmails(patch.email_recipients);
    }
    try {
      await onUpdate(patch);
    } catch {
      if (
        patch.email_recipients !== undefined &&
        desiredEmailsRef.current === patch.email_recipients
      ) {
        desiredEmailsRef.current = prevEmails;
        setLocalEmails(prevEmails);
      }
    }
  };

  const updateCategory = async (
    key: keyof WritableNotificationCategories,
    value: boolean,
  ): Promise<void> => {
    let rollbackValue: boolean | undefined;
    desiredCategoriesRef.current = { ...desiredCategoriesRef.current, [key]: value };
    setLocalCategories((prev) => {
      rollbackValue = prev[key];
      return { ...prev, [key]: value };
    });
    try {
      await onUpdate({ categories: { [key]: value } });
    } catch {
      if (rollbackValue !== undefined && desiredCategoriesRef.current[key] === value) {
        desiredCategoriesRef.current = { ...desiredCategoriesRef.current, [key]: rollbackValue };
        setLocalCategories((prev) => ({ ...prev, [key]: rollbackValue }));
      }
    }
  };

  return (
    <div className={cn('space-y-6', className)}>
      <SettingsSectionHeading title="Notifications" pill="IT" />

      {/* ── Card 1: Slack ────────────────────────────────────────────────────── */}
      <SlackDestinationCard
        slack={slack}
        onUpdate={onUpdate}
        onSetWebhook={onSetSlackWebhook}
        onRemoveWebhook={onRemoveSlackWebhook}
      />

      {/* ── Card 2: Email recipients ──────────────────────────────────────────── */}
      <EmailRecipientsCard emailRecipients={localEmails} onUpdate={handleEmailUpdate} />

      {/* ── Card 3: What to send ──────────────────────────────────────────────── */}
      <SectionCard headerBar title="What to send">
        {anyCategoryOn && !emailDeliveryConfigured && (
          <div className="px-5 pt-4">
            <Alert variant="warning" data-testid="notifications-no-sender">
              <AlertTriangle aria-hidden="true" />
              <AlertTitle>Nothing can be emailed yet</AlertTitle>
              <AlertDescription>
                <p>
                  These categories are on, but this install has no email sender, so no message
                  leaves the building.
                </p>
                <SenderSteps cloud={cloud} />
              </AlertDescription>
            </Alert>
          </div>
        )}
        {emailDeliveryConfigured && emailFailure && (
          <div className="px-5 pt-4">
            <Alert variant="warning" data-testid="notifications-email-failure">
              <AlertTriangle aria-hidden="true" />
              <AlertTitle>Email is not sending</AlertTitle>
              <AlertDescription>
                <p>
                  <FailureCause
                    reason={emailFailure.reason}
                    cloud={cloud}
                    transport={notifications.email_transport}
                  />{' '}
                  Last attempt {absolute(emailFailure.at)}. This clears when the next email goes
                  out. {guide && <GuideLink href={guide} />}
                </p>
              </AlertDescription>
            </Alert>
          </div>
        )}
        <div className="divide-y divide-border">
          <ToggleRow
            label="App health alerts"
            helper="When an app keeps crashing or failing requests, and for administrators when its data access spikes or it calls an integration it has no grant for. Posted to Slack."
            checked={localCategories.health_alerts}
            onCheckedChange={(value) => {
              void updateCategory('health_alerts', value);
            }}
          />
          <ToggleRow
            label="Email health alerts"
            helper="Also email the app's owner and administrators. Off by default."
            checked={localCategories.health_alert_email}
            disabled={!localCategories.health_alerts}
            onCheckedChange={(value) => {
              void updateCategory('health_alert_email', value);
            }}
          />
          <ToggleRow
            label="Pending permission requests"
            checked={localCategories.permission_requests}
            onCheckedChange={(value) => {
              void updateCategory('permission_requests', value);
            }}
          />
          <ToggleRow
            label="Requests to connect a new system"
            checked={localCategories.connection_requests}
            onCheckedChange={(value) => {
              void updateCategory('connection_requests', value);
            }}
          />
          <ToggleRow
            label="Weekly digest"
            helper="A Monday roll-up of Knowledge awaiting a decision, queues nobody has actioned, and how your apps were used."
            checked={localCategories.weekly_digest}
            onCheckedChange={(value) => {
              void updateCategory('weekly_digest', value);
            }}
          />
        </div>
      </SectionCard>
    </div>
  );
}
